import { translateDesktopError } from '@/tauri/errors';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { toast } from '@heidi/design-system/toast';
import i18next from 'i18next';
import type { IntlShape } from 'react-intl';

const NO_DOWNLOAD_URL_CODE = 'CHRONICLE_FIRMWARE_METADATA_FAILED';
const ALREADY_ACTIVE_CODE = 'CHRONICLE_OTA_ALREADY_ACTIVE';
const UNSYNCED_REMAIN_CODE = 'CHRONICLE_UNSYNCED_RECORDINGS_REMAIN';
const DEVICE_NOT_READY_CODE = 'CHRONICLE_OTA_DEVICE_NOT_READY';

const INTERNAL_GUARD_KEYS: Record<string, string> = {
  RECORDING_ACTIVE: 'settings.chronicle.guard.recordingActive',
  BULK_SYNC_ACTIVE: 'settings.chronicle.guard.bulkSyncActive',
};

const TITLE_KEY = 'settings.chronicle.error.firmwareUpdateFailedTitle';
const TITLE_FALLBACK = 'Firmware update failed';
const DESCRIPTION_FALLBACK = 'Please try the update again.';
const ALREADY_ACTIVE_FALLBACK = 'A firmware update is already in progress.';
const UNSYNCED_REMAIN_MESSAGE = {
  id: 'bDAao/owUT',
  defaultMessage:
    'Unsynced sessions are still on your device. Sync them before upgrading.',
} as const;

function translate(key: string, fallback: string): string {
  const translated = i18next.t(key, { defaultValue: '' });
  return translated && translated !== key ? translated : fallback;
}

function getErrorCode(error: unknown): string | undefined {
  const code = (error as { errorCode?: unknown } | null | undefined)?.errorCode;
  return typeof code === 'string' ? code : undefined;
}

function describeFailure(
  errorCode: string | undefined,
  message: string,
  intl?: Pick<IntlShape, 'formatMessage'>
): string {
  if (errorCode === UNSYNCED_REMAIN_CODE || errorCode === DEVICE_NOT_READY_CODE) {
    return (
      intl?.formatMessage(UNSYNCED_REMAIN_MESSAGE) ??
      UNSYNCED_REMAIN_MESSAGE.defaultMessage
    );
  }
  if (errorCode === 'CHRONICLE_INTERNAL') {
    const match = Object.entries(INTERNAL_GUARD_KEYS).find(([token]) =>
      message.includes(token)
    );
    if (match) return translate(match[1], DESCRIPTION_FALLBACK);
  }
  return translateDesktopError(message, errorCode) || DESCRIPTION_FALLBACK;
}

function showFailureToast(
  errorCode: string | undefined,
  message: string,
  intl?: Pick<IntlShape, 'formatMessage'>
): void {
  toast({
    title: translate(TITLE_KEY, TITLE_FALLBACK),
    description: describeFailure(errorCode, message, intl),
    tone: 'destructive',
  });
}

export interface OtaStartRejection {
  errorCode?: string;
  message: string;
  /** False when an update was already running — a no-op, not a failure. */
  isFailure: boolean;
}

export function reportOtaStartRejected(
  source: string,
  error: unknown,
  intl?: Pick<IntlShape, 'formatMessage'>
): OtaStartRejection {
  const message = error instanceof Error ? error.message : String(error);
  const errorCode = getErrorCode(error);

  if (errorCode === ALREADY_ACTIVE_CODE) {
    logger.warn({ msg: `[${source}] OTA start ignored — already running`, errorCode });
    toast({
      title: translateDesktopError(message, errorCode) || ALREADY_ACTIVE_FALLBACK,
    });
    return { errorCode, message, isFailure: false };
  }

  logger.error({ msg: `[${source}] OTA start rejected`, errorCode, error: message });
  showFailureToast(errorCode, message, intl);
  return { errorCode, message, isFailure: true };
}

export function reportOtaStartMissingUrl(
  source: string,
  intl?: Pick<IntlShape, 'formatMessage'>
): void {
  logger.error({
    msg: `[${source}] OTA start aborted — no firmware download URL`,
    errorCode: NO_DOWNLOAD_URL_CODE,
  });
  showFailureToast(NO_DOWNLOAD_URL_CODE, '', intl);
}
